import { useSelector } from 'react-redux';
import {
  EarlyCareSlideOutContent,
  FullCareSlideOutContent,
  ModerateCareSlideOutContent,
  OnboardingChrome,
  Badge,
  CustomizePlanButtonGroup,
  VerticalPhaseCard,
} from '../components';
import { OnboardingCostFactors } from '.';
import { selectClient } from '../model';
import {
  CarePhaseDef,
  carePhaseDefList,
  CarePhase,
  isNullOrUndefined,
  carePhaseDefs,
  formatDurationYears,
  formatInteger,
  isDurationCustom,
} from '@shared';
import { SlideContent } from '../components/SlideContent';

import { FunctionComponent, useEffect, useState } from 'react';
import { CarePhaseDataItem } from '../components/PhaseCard';
import { Clock4Icon, HandHelping, Accessibility, HeartPulse } from 'lucide-react';

function findEntryByValue(value: CarePhase): CarePhaseDef | undefined {
  return carePhaseDefList.find(entry => entry.value === value);
}

function determineTimelineText(client: Client, carePhase: CarePhase, phaseStartAge: number | null) {
  const {
    intakeSurvey: { clientCurrentCarePhase },
  } = client;

  const phaseIndex = carePhaseDefs[carePhase].index;
  const phaseStartAgeText = phaseStartAge ? `${Math.round(phaseStartAge)}` : '';
  // present if current phase is equal to the phase
  if (clientCurrentCarePhase === carePhase) {
    return `Since age ${phaseStartAgeText}`;
  }

  // future if current phase is null or undefined
  if (isNullOrUndefined(clientCurrentCarePhase)) {
    return `Expected age ${phaseStartAgeText}`;
  }

  // past if current phase index is greater than the phase index
  const clientCurrentCarePhaseDef = findEntryByValue(clientCurrentCarePhase);
  if (clientCurrentCarePhaseDef!.index > phaseIndex) {
    return `Past`;
  }

  // future if current phase index is less than the phase index
  return `Expected age ${phaseStartAgeText}`;
}

function determineTimelineTooltip(client: Client, carePhase: CarePhase) {
  const {
    intakeSurvey: { clientPhaseStartAges, clientCurrentCarePhase, clientBirthYear },
    clientPhasePredictedStartYears,
  } = client;

  const phaseIndex = carePhaseDefs[carePhase].index;
  // present if current phase is equal to the phase
  if (clientCurrentCarePhase === carePhase) {
    return `Since ${Math.round(clientPhaseStartAges[carePhase]! + clientBirthYear)}`;
  }

  // future if current phase is null or undefined
  if (isNullOrUndefined(clientCurrentCarePhase)) {
    return `Expected ${Math.round(clientPhasePredictedStartYears[carePhase]!)}`;
  }

  // past if current phase index is greater than the phase index
  const clientCurrentCarePhaseDef = findEntryByValue(clientCurrentCarePhase);
  if (clientCurrentCarePhaseDef!.index > phaseIndex) {
    return `Past`;
  }

  // future if current phase index is less than the phase index
  return `Expected ${Math.round(clientPhasePredictedStartYears[carePhase]!)}`;
}

function determinePreviousSlide() {
  return OnboardingCostFactors;
}

interface CarePhaseProps {
  carePhase: CarePhase;
  slidePage: FunctionComponent<ClientIdProps>;
}

const CARE_PHASE_CONFIGS: Omit<CarePhaseDataItem, keyof ReturnType<typeof getCarePhaseData>>[] = [
  {
    value: CarePhase.earlyCare,
    carePhase: carePhaseDefList[0].label,
    adlRange: '1-2',
    careSettingOptions: 'Home and Independent Living',
    adlRangeIcon: <HandHelping className="h-5 w-5" />,
    slideOutContent: <EarlyCareSlideOutContent />,
  },
  {
    value: CarePhase.moderateCare,
    carePhase: carePhaseDefList[1].label,
    adlRange: '3-4',
    careSettingOptions: 'Home and Assisted Living',
    adlRangeIcon: <Accessibility className="h-5 w-5" />,
    slideOutContent: <ModerateCareSlideOutContent />,
  },
  {
    value: CarePhase.fullCare,
    carePhase: carePhaseDefList[2].label,
    adlRange: '5-6',
    careSettingOptions: 'Home and Nursing Home',
    adlRangeIcon: <HeartPulse className="h-5 w-5" />,
    slideOutContent: <FullCareSlideOutContent />,
  },
];

interface CareTimelineDurationProps {
  clientHasStartedLtc: boolean;
  ltcDurationYears: number;
  phaseCosts: SinglePhaseCosts[];
}

function CareTimelineDuration({
  clientHasStartedLtc,
  ltcDurationYears,
  phaseCosts,
}: CareTimelineDurationProps) {
  return (
    <div className="bg-primary-50 flex w-max items-start gap-1.5 rounded-md px-2 py-1.5 text-base">
      <Clock4Icon className="size-4 translate-y-[3.5px]" />
      <span className="flex flex-col gap-x-1 sm:flex-row">
        <p className="w-max">{`Estimated ${clientHasStartedLtc ? 'remaining ' : ''}care duration`}</p>
        <p className="text-primary w-max font-semibold">
          {`${formatDurationYears(ltcDurationYears)} years (${formatInteger(
            ltcDurationYears * 12,
          )} months)`}
          {isDurationCustom(phaseCosts) && (
            <Badge
              color="gray"
              label="edited"
              className="ml-2 bg-violet-50"
            />
          )}
        </p>
      </span>
    </div>
  );
}

function OnboardingCarePhases({ carePhase, slidePage }: CarePhaseProps) {
  const client = useSelector(selectClient)!;
  const {
    clientId,
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc, clientCurrentCarePhase },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    phaseCosts,
  } = client;

  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;

  const carePhaseData: CarePhaseDataItem[] = CARE_PHASE_CONFIGS.map(config => ({
    ...config,
    ...getCarePhaseData(client, config.value),
  }));

  // Animation state for staggered reveal
  const [visiblePhases, setVisiblePhases] = useState<CarePhase[]>([]);
  const [visibleLines, setVisibleLines] = useState<CarePhase[]>([]);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    const baseDelay = 150;
    const cardDuration = 300;
    const lineDuration = 150;

    carePhaseData.forEach((phase, index) => {
      const cardDelay = baseDelay + index * (cardDuration + lineDuration);
      const cardTimer = setTimeout(() => {
        setVisiblePhases(prev => [...prev, phase.value]);
      }, cardDelay);
      timers.push(cardTimer);

      if (index < carePhaseData.length - 1) {
        const lineDelay = cardDelay + cardDuration;
        const lineTimer = setTimeout(() => {
          setVisibleLines(prev => [...prev, phase.value]);
        }, lineDelay);
        timers.push(lineTimer);
      }
    });

    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);

  return (
    <OnboardingChrome
      slidePage={slidePage}
      nextButtonLabel={`Review ${carePhaseDefs[carePhase].label}`}
      previousSlidePage={determinePreviousSlide()}
    >
      <SlideContent
        headerText={`${clientFirstName}'s care timeline`}
        headerSubText={
          <CareTimelineDuration
            clientHasStartedLtc={clientHasStartedLtc}
            ltcDurationYears={ltcDurationYears}
            phaseCosts={phaseCosts}
          />
        }
      >
        <CustomizePlanButtonGroup
          client={client}
          className="mt-4 lg:mt-0"
        />

        {/* Vertical phase cards layout */}
        <div className="mt-10 w-full max-w-2xl lg:mt-12">
          {carePhaseData.map((phase, index) => {
            const isLastPhase = index === carePhaseData.length - 1;
            const phaseNumber = index + 1;

            return (
              <VerticalPhaseCard
                key={phase.carePhase}
                phase={phase}
                phaseNumber={phaseNumber}
                isLastPhase={isLastPhase}
                isVisible={visiblePhases.includes(phase.value)}
                isLineVisible={visibleLines.includes(phase.value)}
                carePhase={carePhase}
                clientId={clientId}
                clientHasStartedLtc={clientHasStartedLtc}
                clientCurrentCarePhase={clientCurrentCarePhase}
                timelineText={determineTimelineText(client, phase.value, phase.phaseStartAge)}
                timelineTooltip={determineTimelineTooltip(client, phase.value)}
              />
            );
          })}
        </div>
      </SlideContent>
    </OnboardingChrome>
  );
}

function getCarePhaseData(client: Client, carePhase: CarePhase) {
  const {
    intakeSurvey: { clientPhaseStartAges, clientBirthYear, clientCurrentCarePhase },
    phaseCosts,
    careEnvironmentSelections,
    clientPhasePredictedStartYears,
  } = client;

  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];
  const phaseCareMonthsNeeded = selectedCarePhaseCosts.phaseCareMonthsNeeded;
  const phaseInflatedProfessionalShareCost =
    selectedCarePhaseCosts.phaseInflatedProfessionalShareCost;
  const predictedPhaseStartYear = clientPhasePredictedStartYears[carePhase];
  const predictedPhaseStartAge = predictedPhaseStartYear
    ? predictedPhaseStartYear - clientBirthYear
    : clientPhaseStartAges[carePhase]!;

  const selectedCareEnvironment = careEnvironmentSelections[carePhase] ?? null;
  const phaseStartAge =
    clientCurrentCarePhase === carePhase ? clientPhaseStartAges[carePhase] : predictedPhaseStartAge;

  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;

  return {
    phaseStartAge,
    phaseCareMonthsNeeded,
    selectedCareEnvironment,
    phaseInflatedProfessionalShareCost,
    isDurationCustom,
  };
}

export function OnboardingCarePhasesPhaseOne({ clientId }: ClientIdProps) {
  return (
    <OnboardingCarePhases
      carePhase={carePhaseDefs.earlyCare.value}
      slidePage={OnboardingCarePhasesPhaseOne}
    />
  );
}
